import { Page, Pagination, Rsp } from "@repo/types";
import { getHttpClient } from "./http";
import type {
  CompletedOrderCount,
  OrderDetailItem,
  OrderListQuery,
  PaymentCallbackRequest,
  PaymentCallbackResponse,
  PayOrderRequest,
  PayOrderResponse,
  PeerOrderItem,
} from "./orders.type";

export const OrdersAPI = {
  getCompletedOrderCount(uid: string) {
    return getHttpClient().get<Rsp<CompletedOrderCount>>({
      url: `/orders/completed-count?uid=${encodeURIComponent(uid)}`,
    });
  },

  /**
   * 查询订单列表。分页字段与 App 一致：`total` / `total_pages`（不是 `total_count` / `page_total`）。
   * @param page 页码
   * @param pageSize 每页大小
   * @param role 角色：buyer 或 seller。工作台固定传 `"seller"`
   * @param query 筛选与排序，见 `OrderListQuery`
   */
  getOrderList(page: number, pageSize: number, role: string, query: OrderListQuery = {}) {
    const q = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
      role,
    });
    if (query.status) q.set("status", query.status);
    if (query.title) q.set("title", query.title);
    if (query.bizId != null) q.set("biz_id", String(query.bizId));
    if (query.startTime != null) q.set("start_time", String(query.startTime));
    if (query.sortBy) {
      q.set("sort_by", query.sortBy);
      q.set("sort_desc", query.sortDesc ? "true" : "false");
    }
    if (query.excludeClosed) q.set("exclude_closed", "true");
    return getHttpClient().get<Rsp<Pagination<OrderDetailItem[]>>>({
      url: `/orders?${q}`,
    });
  },

  /**
   * 查询订单详情
   */
  getOrderDetail(orderId: string) {
    return getHttpClient().get<Rsp<OrderDetailItem>>({
      url: `/orders/${orderId}`,
    });
  },

  /**
   * 统一收银台发起支付。第三方支付（支付宝/微信）返回 `pay_code` 用于拉起支付。
   */
  payOrder(data: PayOrderRequest) {
    return getHttpClient().post<Rsp<PayOrderResponse>>({
      url: "/orders/pay",
      data,
    });
  },

  /**
   * 支付完成回调：服务端主动向支付中心查询支付结果，返回是否已支付。
   */
  paymentCallback(data: PaymentCallbackRequest) {
    return getHttpClient().post<Rsp<PaymentCallbackResponse>>({
      url: "/orders/payment/callback",
      data,
    });
  },
  /**
   * 聊天订单面板：与聊天对方之间最近的有效订单，双方角色由后端根据 peer_uid 判断（item.role）。
   */
  getPeerOrders(peerUid: string, page = 1, pageSize = 10) {
    const q = new URLSearchParams({
      peer_uid: peerUid,
      page: String(page),
      page_size: String(pageSize),
    });
    return getHttpClient().get<Rsp<Page<PeerOrderItem[]>>>({
      url: `/orders/peer-orders?${q}`,
    });
  },
};
